// The sport plugin contract. See docs/design/contracts/SPORT_DEFINITION.md
// for the reasoning; this file is that document as code.
//
// Rules for anyone touching this file:
// - The core never compares a PositionId, SlotId, or StatKey to a literal.
//   It looks them up in the sport's tables.
// - Everything here is data or a pure function. No I/O, no dates from the
//   clock (callers pass `now`), no UI.

// ── Identity ──────────────────────────────────────────────────────────────

export type SportId = 'football' | 'basketball' | 'baseball' | 'hockey';
export const SPORT_IDS: readonly SportId[] = ['football', 'basketball', 'baseball', 'hockey'];

/** A season is named by the calendar year it starts in. */
export type Season = number;
/** 1-based index of a period (matchup window) within a season. */
export type PeriodIndex = number;

/** Opaque, sport-owned. 'QB', 'PG', 'SP', ... */
export type PositionId = string;
/** Opaque, sport-owned. 'FLEX', 'UTIL', 'BN', 'IR', ... */
export type SlotId = string;
/** Opaque, sport-owned. 'pass_yd', 'reb', 'era', ... */
export type StatKey = string;
/** A player's raw stats for one game or one period. Missing key means 0. */
export type StatLine = Readonly<Record<StatKey, number>>;

// ── Real-world teams ──────────────────────────────────────────────────────

export interface TeamPalette {
  /** Hex, '#rrggbb'. */
  primary: string;
  secondary: string;
  accent?: string;
  /** Text color that passes contrast on `primary`. */
  onPrimary: string;
}

export interface ProTeam {
  /** Sport-scoped, stable. 'MIN', 'LAL'. */
  id: string;
  /** 'Minnesota Vikings' */
  name: string;
  /** 'Vikings' */
  shortName: string;
  /** 'MIN' */
  abbreviation: string;
  /** 'Minnesota' */
  location: string;
  conference?: string;
  division?: string;
  palette: TeamPalette;
}

// ── Calendar ──────────────────────────────────────────────────────────────

export type LineupPeriodKind = 'weekly' | 'daily';

export type LockRuleId =
  /** Each starter locks at their own game's start. */
  | 'per-player-game'
  /** The whole lineup locks when the window's first game starts. */
  | 'first-game'
  /** Locks at a league-configured wall-clock time in the league's time zone. */
  | 'fixed-time';

export const LOCK_RULE_IDS: readonly LockRuleId[] = ['per-player-game', 'first-game', 'fixed-time'];

export interface Game {
  id: string;
  sport: SportId;
  season: Season;
  /** ISO instant. */
  start: string;
  /** ProTeam.id */
  homeTeam: string;
  awayTeam: string;
  status: 'scheduled' | 'live' | 'final' | 'postponed' | 'canceled';
  /** The provider's own period number, when it has one (NFL week). */
  periodHint?: PeriodIndex;
}

export interface LineupWindow {
  /** Stable within a season: '2026-w03', '2026-11-04'. */
  id: string;
  /** ISO instant, inclusive. */
  start: string;
  /** ISO instant, exclusive. */
  end: string;
}

export interface Period {
  season: Season;
  index: PeriodIndex;
  /** 'Week 3', 'Week of Nov 4' */
  label: string;
  start: string;
  end: string;
  /** At least one, ordered, non-overlapping, covering [start, end). */
  windows: readonly LineupWindow[];
  flags: {
    playoffEligible: boolean;
    /** ProTeam ids with no game this period. */
    proBye: readonly string[];
  };
}

export interface SeasonCalendar {
  /**
   * The season as periods at matchup granularity. `lineupPeriod` decides how
   * many lineup windows each period contains. Computed from the real schedule
   * so a rescheduled game moves the right lock.
   */
  periods(
    season: Season,
    lineupPeriod: LineupPeriodKind,
    games: readonly Game[],
  ): readonly Period[];
  /** Suggested regular-season span for the league wizard. The league decides. */
  defaultRegularSeasonPeriods(season: Season): { first: PeriodIndex; last: PeriodIndex };
  /** Suggested playoff periods for the wizard. */
  defaultPlayoffPeriods(season: Season): readonly PeriodIndex[];
  /** For the home screen: is this season live at `now` (ISO instant)? */
  isInSeason(season: Season, now: string): boolean;
  /**
   * The days a league's weekly waiver run may fall on, 0 for Sunday, in the
   * league's own time zone (B-509). A run before a week's last game leaves
   * everyone who played in it on waivers through the next week's games, so a
   * sport whose weeks end early in the week names the days after. Absent: any
   * day.
   */
  waiverWeekdays?: readonly number[];
}

// ── Roster shape ──────────────────────────────────────────────────────────

export interface PositionDef {
  id: PositionId;
  /** 'Running Back' */
  name: string;
  /** 'offense' | 'defense' | 'special' | 'hitter' | 'pitcher' | 'skater' | 'goalie' ... */
  group: string;
  /** Design token name for the position pill. */
  color: string;
  sortOrder: number;
}

export interface SlotDef {
  id: SlotId;
  name: string;
  /** Empty means any position (bench, reserve, taxi). */
  eligible: readonly PositionId[];
  kind: 'starter' | 'bench' | 'reserve' | 'taxi';
  sortOrder: number;
}

export interface RosterPreset {
  id: string;
  name: string;
  slots: readonly { slot: SlotId; count: number }[];
  maxPerPosition?: Readonly<Record<PositionId, number>>;
  reserve: {
    slot: SlotId;
    count: number;
    /** InjuryStatusDef ids that may sit in reserve. */
    eligibleStatuses: readonly string[];
  } | null;
  taxi: { slot: SlotId; count: number; maxYearsExperience: number } | null;
}

export interface InjuryStatusDef {
  /** 'Q', 'D', 'O', 'IR', 'DTD', ... */
  id: string;
  name: string;
  severity: 'none' | 'warn' | 'likely-out' | 'out';
  reserveEligible: boolean;
  /**
   * How long it holds: `game`, one game's designation, given by that week's
   * report and saying nothing of the next ('Q', 'D', 'O'); `standing`, until
   * it is lifted (injured reserve, a suspension). A planner reads a game's in
   * its week alone (PLAN-2026-11-01-the-injury-report-dated-and-scoped §3).
   */
  lasts: 'game' | 'standing';
}

// ── Stats ─────────────────────────────────────────────────────────────────

export type StatKind =
  /** Whole-number events: receptions, rebounds, strikeouts. */
  | 'count'
  /** Yardage or other continuous totals. */
  | 'yards'
  /** Derived from two other stats; never scored directly, may be a category. */
  | 'ratio'
  /** Scored by which band the value falls in: points allowed. */
  | 'tiered';

export interface StatDef {
  key: StatKey;
  name: string;
  shortName: string;
  /** Groups the rule editor: 'passing', 'rebounding', 'pitching'. */
  category: string;
  kind: StatKind;
  ratio?: { numerator: StatKey; denominator: StatKey };
  /** Positions this stat is meaningful for; undefined = all. */
  positions?: readonly PositionId[];
  /** May appear in a ScoringConfig. */
  scorable: boolean;
  /** May appear in a CategoryConfig. */
  categorizable: boolean;
  /** For categories: ERA, turnovers. */
  lowerIsBetter?: boolean;
  /** Display precision. */
  decimals: number;
  /**
   * How a scoring table says this stat to somebody who has never played
   * (PLAN-2026-10-18-scoring-anyone-can-read §3). Absent: the stat is not
   * offered in the scoring editor, and a rule on it reads by `name`.
   */
  scoring?: StatScoringWords;
}

/** One stat in a scoring table's words. Added 2026-10-18 on Ken's go. */
export interface StatScoringWords {
  /** The row for one of it: 'Touchdown pass', 'Each catch', 'Interception thrown'. */
  label: string;
  /** What is counted, plural, for "Every 25 passing yards" and "300 passing yards in a game". */
  plural: string;
  /** The units a league may count it in ("every 5 completions"), when more than ones. */
  units?: readonly number[];
  /** The unit a table shows it in when a rule does not say: 25 for passing yards. */
  unit?: number;
  /** One band of a series, so equal neighbors read as one row: field goals by distance. */
  range?: { series: string; min: number; max: number | null };
  /** A tiered stat's bands: what they count, the zero band's own name, and the bands a league starts from. */
  bands?: {
    noun: string;
    zero?: string;
    defaults?: readonly { min: number; max: number | null }[];
  };
  /** Words an experienced player searches with: 'PPR', 'rec'. */
  aliases?: readonly string[];
}

/**
 * A scoring table's shape for this sport: the kinds of play, and per position
 * which kinds come first and which rows always show (§2.2). The core reads it;
 * it never names a stat or a position itself.
 */
export interface ScoringLayout {
  /** Kinds of play, each a heading over its stats, in reading order. */
  groups: readonly { id: string; title: string; stats: readonly StatKey[] }[];
  /** One per position, in the order the table lists them. */
  sections: readonly {
    position: PositionId;
    /** 'Quarterbacks'. */
    title: string;
    /** The groups this position earns its points in, in that order; the rest sit under "more". */
    main: readonly string[];
    /** Rows shown even at 0, because a newcomer looks for them: a catch. */
    always: readonly StatKey[];
  }[];
  /** A series' label, `{range}` standing for the merged span: 'Field goal, {range} yards'. */
  series?: readonly { id: string; label: string }[];
}

/**
 * The questions the create form asks about scoring, and the presets their
 * answers give (§2.1). A question is a switch (two options) or a choice.
 */
export interface ScoringSetup {
  questions: readonly {
    id: string;
    label: string;
    style: 'switch' | 'choice';
    default: string;
    options: readonly {
      value: string;
      label: string;
      /** What it means, in one sentence. */
      sentence: string;
      /** How the setup's name says it: 'points for catches', "a team's defense". */
      phrase: string;
      /** False for an option the sport cannot play yet; the form shows it greyed. */
      available: boolean;
    }[];
  }[];
  /** The scoring and roster presets a full set of answers gives. Pure. */
  presets(answers: Readonly<Record<string, string>>): { scoring: string; roster: string };
}

// ── Scoring presets (shapes owned by @fsp/domain; referenced here by name) ──

/** Structural copy of the domain's ScoringConfig so this package stays sport- and domain-free. */
export interface ScoringConfigLike {
  presetId: string | null;
  units: readonly {
    stat: StatKey;
    points: number;
    per: number;
    rounding: 'none' | 'floor' | 'round';
    positions: readonly PositionId[] | null;
  }[];
  bonuses: readonly {
    stat: StatKey;
    threshold: number;
    points: number;
    scope: 'game' | 'period';
    positions: readonly PositionId[] | null;
  }[];
  tiers: readonly {
    stat: StatKey;
    bands: readonly { min: number; max: number | null; points: number }[];
    scope: 'game' | 'period';
    positions: readonly PositionId[] | null;
  }[];
  decimals: number;
}

export interface CategoryConfigLike {
  presetId: string | null;
  categories: readonly { stat: StatKey; weight: number }[];
  ties: 'half' | 'ignore';
}

export interface ScoringPreset {
  id: string;
  name: string;
  description: string;
  config: ScoringConfigLike;
}

export interface CategoryPreset {
  id: string;
  name: string;
  description: string;
  config: CategoryConfigLike;
}

// ── Formats ───────────────────────────────────────────────────────────────

export type LeagueFormatId =
  | 'h2h-points'
  | 'h2h-categories'
  | 'h2h-each-category'
  | 'rotisserie'
  | 'points-total'
  | 'all-play'
  | 'best-ball'
  | 'elimination';

export const LEAGUE_FORMAT_IDS: readonly LeagueFormatId[] = [
  'h2h-points',
  'h2h-categories',
  'h2h-each-category',
  'rotisserie',
  'points-total',
  'all-play',
  'best-ball',
  'elimination',
];

export const CATEGORY_FORMATS: ReadonlySet<LeagueFormatId> = new Set([
  'h2h-categories',
  'h2h-each-category',
  'rotisserie',
]);

// ── Display ───────────────────────────────────────────────────────────────

export interface DisplayRules {
  /** '5 rec, 82 yd, 1 TD' */
  summarizeLine(line: StatLine, positions: readonly PositionId[]): string;
  tableColumns(positionGroup: string, mode: 'casual' | 'pro'): readonly StatKey[];
  gameLogColumns(positionGroup: string): readonly StatKey[];
  windowLabel(window: LineupWindow, lineupPeriod: LineupPeriodKind): string;
  /**
   * A position's own columns, each at the least detail level it shows at
   * (DIRECTION U29, PLAN-2026-10-26-numbers-that-fit-the-position §9.2): the
   * stats it earns its points by and the few that drive them. Absent, or
   * null for a position, a table reads its group's box score
   * (`gameLogColumns`), the casual ones at Simple. Added 2026-10-26.
   */
  statSheet?(position: PositionId): readonly StatSheetColumn[] | null;
}

/** The least detail level a column shows at (DETAIL_LEVELS.md). */
export type SheetLevel = 'simple' | 'standard' | 'everything';

/**
 * How a stat sheet's column is worked out from a player's lines, over the
 * games a table reads: one stat as it is; several added; a rate of two sums
 * (yards a carry, completions of attempts as a percent); one stat a game
 * (points allowed); made of attempted, the attempts being the made and the
 * missed together (field goals); or his share of his club's total of a stat,
 * which only a host that keeps clubs' totals can fill (targets).
 */
export type SheetFigure =
  | { stat: StatKey }
  | { sum: readonly StatKey[] }
  | { rate: readonly StatKey[]; per: readonly StatKey[]; percent?: boolean }
  | { perGame: StatKey }
  | { made: readonly StatKey[]; missed: readonly StatKey[] }
  | { share: StatKey };

export interface StatSheetColumn {
  /** The stat's key for a `stat` figure; the column's own id otherwise. */
  key: string;
  /** The head: 'Rush Yd', 'Cmp%'. Absent, the stat's `shortName`. */
  label?: string;
  /** What it is, said when asked. Absent, the stat's `name`. */
  name?: string;
  level: SheetLevel;
  figure: SheetFigure;
  /** Places it is shown to. Absent, the stat's `decimals`, or 0; a rate's 1. */
  decimals?: number;
}

// ── League names (B-430) ──────────────────────────────────────────────────
//
// The words the create form's *Suggest one* builds a league name from. The
// core holds the words every fantasy sport shares (`@fsp/domain`'s
// `leagueNameIdea`); a sport adds its own to the same lists, so football's
// Sundays and Hail Marys never appear in the core. Display only: no rule
// ever reads them.

/**
 * A list of name words, by the part it plays in a name. Every word in a list
 * has to read right in every pattern that list is used in:
 *
 * - `names`: a whole name as it is. *Fourth and Long*.
 * - `describing` + `gatherings`: *The {Overconfident} {Supper Club}*.
 * - `subjects` + `societies`: *{Pick Six} {Appreciation Society}*.
 * - `moments` + `feelings`: *{Thursday Night} {Panic}*.
 * - `hangouts` + `events`: *The {Kitchen Table} {Invitational}*.
 * - `collections`: *The League of {Moral Victories}*.
 */
export type LeagueNameSlot =
  | 'names'
  | 'describing'
  | 'gatherings'
  | 'subjects'
  | 'societies'
  | 'moments'
  | 'feelings'
  | 'hangouts'
  | 'events'
  | 'collections';

export type LeagueNameWords = Partial<Readonly<Record<LeagueNameSlot, readonly string[]>>>;

// ── The weekly challenge ──────────────────────────────────────────────────

/**
 * The weekly challenge (SCENARIOS Q9, PLAN-2026-11-01-the-weekly-challenge):
 * one pro week, the same for everybody. Each week draws `positionsPerWeek`
 * of these positions and, for each, `categoriesPerPosition` of its
 * categories, never two of one family; each drawn position's players are cut
 * into `tiers` tiers by fantasy points a game, and an entry picks one player
 * from every tier, one of them its captain. Added 2026-11-01 on Ken's go.
 */
export interface WeeklyChallengeDef {
  positionsPerWeek: number;
  categoriesPerPosition: number;
  tiers: number;
  /** The captain's points count this many times over: 1.5. */
  captain: number;
  /** A player's tier is measured over his most recent games, across seasons: this many. */
  recentGames: number;
  /** The fewest of those he needs to be in a tier at all. */
  fewestGames: number;
  /**
   * The latest a week opens: the last of these wall-clock moments before its
   * first game. Every week but a season's first opens earlier, at the first
   * kickoff of the week before it (B-620), and a season's first a week
   * before its first game (B-621).
   */
  opens: ChallengeMoment;
  /** And is over at the first of these after its last game, once its lines are in. */
  ends: ChallengeMoment;
  positions: readonly ChallengePositionDef[];
}

/** A day of the week (0 = Sunday), a time 'HH:MM', and the IANA zone they are read in. */
export interface ChallengeMoment {
  weekday: number;
  time: string;
  zone: string;
}

export interface ChallengePositionDef {
  position: PositionId;
  /** Players in each tier. */
  tierSize: number;
  categories: readonly ChallengeCategoryDef[];
}

/**
 * One thing a week can be scored on. Its points are round numbers that make
 * sense for the stat, the same wherever it is scored (a touchdown 6, a catch
 * 1, ten yards 1); the categories need not have an equal say
 * (`scripts/challenge/history.ts --calibrate` prints each one's). A week
 * keeps the points it opened with.
 */
export interface ChallengeCategoryDef {
  /** Stable within the sport: 'pass_yd', 'cmp_pct'. */
  id: string;
  /** 'Passing yards', 'Completion percentage'. */
  name: string;
  /** A column's head where room is short: 'Pass yds', 'Cmp%'. */
  short: string;
  /** One of it, plural, for "1 point per 7 passing yards": 'passing yards'. */
  unit: string;
  /** The stats added together for its value. */
  of: readonly StatKey[];
  /** A rate: its value divided by these stats, and the fewest of them it needs to count at all. */
  rate?: { over: readonly StatKey[]; atLeast: number; percent?: boolean };
  /** A rate counts up from here (completion percentage from 50). Absent: 0. */
  from?: number;
  /** Points for every `every` of it: 1 point for every 10 rushing yards. */
  points: number;
  /** Absent: 1, points for each one. */
  every?: number;
  /** Counts against, as interceptions thrown. A week draws one at most for a position. */
  against?: boolean;
  /** How often a week draws it, against 1 for the others. Absent: 1. */
  weight?: number;
  /** Its kind of strength. A week never draws two of one family for a position. */
  family: string;
  /** Display precision of its value. Absent: 0. */
  decimals?: number;
}

// ── Research (Pro mode) ───────────────────────────────────────────────────

export interface ResearchModuleDef {
  id: string;
  name: string;
  placement: 'player-card' | 'players-table' | 'start-sit' | 'matchup';
  /** Hidden when the adapter can't supply these. */
  requiredStats: readonly StatKey[];
  /** Resolved by the web app's component registry. */
  component: string;
}

// ── Achievements (ACHIEVEMENTS.md §14) ────────────────────────────────────
//
// The account achievements that name the sport's own stat keys and
// positions: *"missed two kicks"*, *"200 rushing yards"*. The engine and the
// families everyone shares are the core's (`@fsp/achievements`); a sport
// supplies only what the core may not name. Added 2026-10-03 on Ken's go.
//
// SERVER-ONLY. A hidden row's name and condition never reach a client, so a
// sport package exports these from an entry of their own that no client
// imports (a lint holds the web app to it), and the definition a client
// bundles carries none.

/** How an achievement shows before it is earned (ACHIEVEMENTS.md §7). */
export type AchievementShows = 'open' | 'hinted' | 'secret';
/** As the league awards': glory, shame, or neither. */
export type AchievementTone = 'glory' | 'shame' | 'fun';
/**
 * A guard a sport's row may name (ACHIEVEMENTS.md §8). `fair-start`: the
 * player was projected at least 5 points when his slot locked and was not
 * ruled out or doubtful then — so misfortune counts and starting someone who
 * will not play does not.
 */
export type SportAchievementGuard = 'fair-start';

/** A bound on a number. Every bound given must hold. */
export interface NumberTest {
  atLeast?: number;
  atMost?: number;
  below?: number;
  exactly?: number;
}

/** A test on one starter's settled week: his stat line, points, position, and when his game was. */
export type StarterTest =
  | { kind: 'stat'; stat: StatKey; test: NumberTest }
  | { kind: 'sum'; stats: readonly StatKey[]; test: NumberTest }
  | { kind: 'points'; test: NumberTest }
  | { kind: 'position'; position: PositionId }
  /** His game kicked off inside one of the sport's `moments`. */
  | { kind: 'moment'; moment: string }
  | { kind: 'all'; of: readonly StarterTest[] }
  | { kind: 'any'; of: readonly StarterTest[] };

/** What earns a sport's row, read when a week settles. */
export type SportAchievementWhen =
  /** One starter passes the test. */
  | { at: 'week-settled'; starter: StarterTest }
  /** The best of the starters passing `higher` outscores the best passing `lower`; both must exist. */
  | { at: 'week-settled'; outscores: { higher: StarterTest; lower: StarterTest } };

export interface SportAchievementDef {
  /** Unique across the core and every sport: an achievement is once per account. */
  id: string;
  /** One of this sport's `families`. */
  family: string;
  shows: AchievementShows;
  name: string;
  /** Plain words for what earns it, shown before earning only when it is open. */
  asks: string;
  /** A riddle, for a hinted row: a feeling or an aftermath, never the stat or an instruction. */
  hint?: string;
  /**
   * The moment, filled from what happened: `{player}`, `{points}`, `{period}`,
   * `{league}`, `{other}` and `{otherPoints}` for `outscores`, and any of the
   * sport's stat keys, or several joined by `+` for their sum
   * (`{fg_miss+xp_miss}`).
   */
  line: string;
  tone: AchievementTone;
  icon: string;
  when: SportAchievementWhen;
  guard: readonly SportAchievementGuard[];
  /** ISO date the row was added, which is when its backfill runs. */
  added: string;
}

export interface AchievementFamilyDef {
  id: string;
  name: string;
  icon: string;
}

/** A stretch of the calendar a row can name: Thanksgiving. */
export interface CalendarMomentDef {
  id: string;
  name: string;
  /** Whether a game kicking off at this instant falls in it. Pure. */
  contains(startIso: string): boolean;
}

export interface SportAchievements {
  families: readonly AchievementFamilyDef[];
  rows: readonly SportAchievementDef[];
  moments: readonly CalendarMomentDef[];
}

// ── The definition ────────────────────────────────────────────────────────

export interface SportDefinition {
  id: SportId;
  name: string;
  icon: string;
  /** Display only, never logic. 'NFL'. */
  proLeagueName: string;

  calendar: SeasonCalendar;

  positions: readonly PositionDef[];
  slots: readonly SlotDef[];
  /** First is the default. */
  rosterPresets: readonly RosterPreset[];
  injuryStatuses: readonly InjuryStatusDef[];

  stats: readonly StatDef[];
  /** First is the default. */
  scoringPresets: readonly ScoringPreset[];
  /** Empty when the sport has no categories play. */
  categoryPresets: readonly CategoryPreset[];
  /** How a scoring table lays this sport out (§2.2). Absent: by stat category, every position alike. */
  scoringLayout?: ScoringLayout;
  /** The create form's scoring questions (§2.1). Absent: the form offers the presets by name. */
  scoringSetup?: ScoringSetup;
  supportedFormats: readonly LeagueFormatId[];

  lineupPeriods: readonly LineupPeriodKind[];
  defaultLineupPeriod: LineupPeriodKind;
  defaultLockRule: LockRuleId;

  proTeams: readonly ProTeam[];
  display: DisplayRules;
  researchModules: readonly ResearchModuleDef[];
  /**
   * How a player's usage is measured (SCENARIOS E8), for a sport that has a
   * sense of it. `share` is the stat whose share of his club's total in a
   * game is his usage — football's targets; `perGame` a stat shown as a
   * per-game average beside it — football's air yards. Optional: a sport
   * without one shows no usage. Added 2026-10-03 on Ken's go.
   */
  usage?: { share: StatKey; perGame?: StatKey };
  /**
   * The sport's own account achievements, its families, and the calendar
   * moments its rows name (ACHIEVEMENTS.md §14). Present only in the
   * definition a server composes: a client's definition carries none, so a
   * hidden row never reaches a browser. Added 2026-10-03 on Ken's go.
   */
  achievements?: SportAchievements;
  /**
   * The sport's own words for league name ideas, added to the core's
   * (`LeagueNameWords`, B-430). Optional: a sport without them gets the
   * core's alone. Added 2026-10-18 on Ken's go.
   */
  leagueNameWords?: LeagueNameWords;
  /**
   * The weekly challenge's positions and categories (SCENARIOS Q9).
   * Optional: a sport without one has no weekly challenge. Added 2026-11-01
   * on Ken's go.
   */
  weeklyChallenge?: WeeklyChallengeDef;
}

// ── Adapter (server-side; types only here) ────────────────────────────────

export interface PlayerRecord {
  providerId: string;
  name: { first: string; last: string; display: string };
  positions: readonly PositionId[];
  /** ProTeam.id, or null for a free agent. */
  proTeam: string | null;
  status: 'active' | 'inactive' | 'retired' | 'practice-squad';
  injury: { status: string; note?: string } | null;
  experienceYears: number;
  externalIds?: Readonly<Record<string, string>>;
}

export interface StatsBatch {
  window: LineupWindow;
  fetchedAt: string;
  /** All games in the window are final. */
  complete: boolean;
  lines: readonly { providerId: string; gameId: string; line: StatLine }[];
}

export interface ProjectionRecord {
  providerId: string;
  period: PeriodIndex;
  line: StatLine;
  source: string;
}

export interface InjuryRecord {
  providerId: string;
  status: string;
  note?: string;
  updatedAt: string;
}

export interface NewsItem {
  id: string;
  providerIds: readonly string[];
  headline: string;
  body?: string;
  source: string;
  url?: string;
  publishedAt: string;
}

export interface SportDataAdapter {
  sport: SportId;
  providerName: string;
  capabilities: {
    players: true;
    schedule: true;
    liveStats: boolean;
    finalStats: true;
    projections: boolean;
    injuries: boolean;
    news: boolean;
    usage: boolean;
  };
  fetchPlayers(season: Season): Promise<readonly PlayerRecord[]>;
  fetchSchedule(season: Season): Promise<readonly Game[]>;
  fetchStats(season: Season, window: LineupWindow): Promise<StatsBatch>;
  fetchProjections?(season: Season, period: PeriodIndex): Promise<readonly ProjectionRecord[]>;
  fetchInjuries?(season: Season): Promise<readonly InjuryRecord[]>;
  fetchNews?(since: string): Promise<readonly NewsItem[]>;
  fetchSeasonState(): Promise<{
    season: Season;
    currentPeriodHint?: PeriodIndex;
    phase: 'pre' | 'regular' | 'post' | 'off';
  }>;
}
